import {
  InsightV2ApiInsightV2Request,
  InsightV2Pagination,
  ResourceSummaryApiGetVmOpenPortsRequest,
  VirtualMachineOpenPortsResponse,
} from '@ariksa/inventory-core/api';
import {
  AlertLogsApiGetGroupTicketsRequest,
  AlertLogsApiGetLogsForIdsRequest,
  AlertLogsApiUpdateTicketFieldRequest,
  AlertLogsResponse,
  AlertPaginatedResponse,
  AlertResponse,
  AlertsApiGetAlertByIdRequest,
  ClientsApiNotifyClientRequest,
  PaginatedGroupTicketsResponse,
  TicketUpdateResponse,
} from '@ariksa/notification/api';
import { ClientsApiUpdateTicketRequest } from '@ariksa/notification/api';
import { call } from 'redux-saga/effects';
import { QueryContext } from 'services/utils/QueryContext';
import { takeLatestAction } from 'services/utils/takeLatestAction';

import {
  AlertVaultAlertService,
  AlertVaultDeliveryService,
  AlertVaultTicketService,
  isAlertVaultEnabled,
} from 'api/alert-vault/client';
import { InventoryService, NotificationService } from 'api/services';
import { doGetAlerts } from 'containers/Findings/FindingsOverview/saga';
import { doGetRiskContext } from 'containers/SharedState/saga';

import { actions } from './slice';

export function* alertDrawerSaga() {
  yield takeLatestAction(actions.getResourceInsight, doGetResourceInsight);
  yield takeLatestAction(actions.createTicket, doCreateTicket);
  yield takeLatestAction(actions.createNotification, doCreateNotification);
  yield takeLatestAction(actions.updateDueDate, doUpdateDueDate);
  yield takeLatestAction(actions.getAlertById, doGetAlertById);
  yield takeLatestAction(actions.getAggregatedAlert, doGetAlerts);
  yield takeLatestAction(actions.updateAlertStatus, doUpdateAlertStatus);
  yield takeLatestAction(actions.getAlertListStatus, doGetalertListStatus);
  yield takeLatestAction(actions.getAlertStatus, doGetAlertStatus);
  yield takeLatestAction(actions.getAlertsByRuleId, doGetAlertsByRuleId);
  yield takeLatestAction(actions.getRiskContext, doGetRiskContext);
  yield takeLatestAction(actions.getChildTickets, doGetChildTickets);
  yield takeLatestAction(actions.getVMOpenPorts, doGetVMOpenPorts);
}

function* doGetResourceInsight(
  ctx: QueryContext<InsightV2Pagination, InsightV2ApiInsightV2Request>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.InsightV2.insightV2(ctx.params),
    { errorMsg: 'Failed to get resource insights!' },
  );
}

function* doGetVMOpenPorts(
  ctx: QueryContext<
    VirtualMachineOpenPortsResponse[],
    ResourceSummaryApiGetVmOpenPortsRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.ResourceSummary.getVmOpenPorts(ctx.params),
    { errorMsg: 'Failed to get virtual machine open ports!' },
  );
}

function* doGetChildTickets(
  ctx: QueryContext<
    PaginatedGroupTicketsResponse,
    AlertLogsApiGetGroupTicketsRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => {
      const alertId = Array.isArray((ctx.params as any).alertId)
        ? (ctx.params as any).alertId[0]
        : (ctx.params as any).alertId;
      return isAlertVaultEnabled() && alertId
        ? AlertVaultTicketService.list({
            alertId,
            pageNumber: (ctx.params as any).pageNumber,
            pageSize: (ctx.params as any).pageSize,
          })
        : NotificationService.AlertsLogs.getGroupTickets(ctx.params);
    },
    { errorMsg: 'Failed to get child tickets!' },
  );
}

function* doCreateTicket(
  ctx: QueryContext<any, ClientsApiNotifyClientRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled() &&
      ctx.params?.notifyClients?.client_id &&
      ctx.params?.notifyClients?.alert_uuid
        ? AlertVaultDeliveryService.create({
            alert_id: ctx.params.notifyClients.alert_uuid,
            client_id: ctx.params.notifyClients.client_id,
            body: ctx.params.notifyClients.ticket_fields?.summary,
            metadata: ctx.params.notifyClients.ticket_fields,
          })
        : NotificationService.Clients.notifyClient(ctx.params),
    { errorMsg: 'Failed to create ticket.' },
  );
}

function* doCreateNotification(
  ctx: QueryContext<any, ClientsApiNotifyClientRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled() &&
      ctx.params?.notifyClients?.client_id &&
      ctx.params?.notifyClients?.alert_uuid
        ? AlertVaultDeliveryService.create({
            alert_id: ctx.params.notifyClients.alert_uuid,
            client_id: ctx.params.notifyClients.client_id,
            body: ctx.params.notifyClients.message,
            metadata: ctx.params.notifyClients.ticket_fields,
          })
        : NotificationService.Clients.notifyClient(ctx.params),
    { errorMsg: 'Failed to send notification.' },
  );
}

export function* doUpdateDueDate(
  ctx: QueryContext<TicketUpdateResponse, AlertLogsApiUpdateTicketFieldRequest>,
) {
  yield call(
    ctx.fetch,
    () => NotificationService.AlertsLogs.updateTicketField(ctx.params),
    { errorMsg: 'Failed to update due date.' },
  );
}

function* doGetAlertById(
  ctx: QueryContext<AlertResponse, AlertsApiGetAlertByIdRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled()
        ? AlertVaultAlertService.get(ctx.params.alertId)
        : NotificationService.Alerts.getAlertById(ctx.params),
    { errorMsg: 'Failed to get issue.' },
  );
}

export function* doUpdateAlertStatus(
  ctx: QueryContext<any, ClientsApiUpdateTicketRequest>,
) {
  yield call(
    ctx.fetch,
    () => {
      const status = String(
        (ctx.params as any).ticketStatus ?? '',
      ).toLowerCase();
      const event =
        status === 'resolved'
          ? 'resolved'
          : status === 'in_progress' || status === 'in progress'
          ? 'in_progress'
          : undefined;
      return isAlertVaultEnabled() && event && (ctx.params as any).alertId
        ? AlertVaultAlertService.lifecycle(
            (ctx.params as any).alertId,
            event as any,
          )
        : NotificationService.Clients.updateTicket(ctx.params);
    },
    { errorMsg: 'Failed to update alert status!' },
  );
}

function* doGetalertListStatus(
  ctx: QueryContext<AlertLogsResponse[], AlertLogsApiGetLogsForIdsRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled() && ctx.params.alertId?.[0]
        ? AlertVaultAlertService.activity(ctx.params.alertId?.[0] ?? '')
        : NotificationService.AlertsLogs.getLogsForIds(ctx.params),
    { errorMsg: 'Failed to get alert status!' },
  );
}

export function* doGetAlertStatus(
  ctx: QueryContext<AlertLogsResponse[], AlertLogsApiGetLogsForIdsRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled() && ctx.params.alertId?.[0]
        ? AlertVaultAlertService.activity(ctx.params.alertId?.[0] ?? '')
        : NotificationService.AlertsLogs.getLogsForIds(ctx.params),
    { errorMsg: 'Failed to get alert status!' },
  );
}

function* doGetAlertsByRuleId(ctx: QueryContext<AlertPaginatedResponse, any>) {
  yield call(
    ctx.fetch,
    () => {
      const params = ctx.params as any;
      const groupId = String(params.groupId ?? '');
      const [groupBy, groupKey] = groupId.split(':', 2);
      const common = {
        accountId: params.accountId,
        environmentId: params.environmentId,
        category: params.alertCategory,
        severity: params.severity,
        status: params.status,
        pageNumber: ctx.page.page_number,
        pageSize: ctx.page.page_size,
      };
      if (groupBy === 'resource' && groupKey) {
        return AlertVaultAlertService.resourceAlerts(groupKey, common);
      }
      // Finding rows group one check across policies; list by its group.
      if (groupBy === 'finding' && groupKey) {
        return AlertVaultAlertService.list({ ...common, groupId: groupKey });
      }
      return AlertVaultAlertService.list({
        ...common,
        policyId: groupBy === 'policy' ? groupKey : params.alertRuleId,
      });
    },
    { cacheKey: 'getAlertsByRuleId' },
  );
}
